import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TuneIcon from '@mui/icons-material/Tune';
import { appColors } from '../../Config/Theme';

export interface CurrentRosterViewBarProps {
  /** Human-readable roster scope, e.g. "Full Roster" or "Accountability Roster". */
  scopeLabel: string;
  /** Filtered result count currently shown in the people list. */
  count: number;
  /** Noun for the count, e.g. "people" (Headcount) or "need attention" (Not Accounted). */
  countNoun?: string;
  /** Active alpha/group filter label, e.g. "A–F". Null when no group filter is active. */
  groupLabel?: string | null;
  /** Concise summary of other active filters, e.g. "Tenant Company: Regus". */
  filterSummary?: string | null;
  /** Whether the current result is narrowed (controls Reset visibility). */
  isNarrowed?: boolean;
  /** Clears the current narrowing (group/filters) while keeping the roster scope. */
  onReset?: () => void;
  /** Reset action label. */
  resetLabel?: string;
  /** Optional "Change View / Filters" action. */
  onChangeView?: () => void;
  /** Label for the change-view action. */
  changeViewLabel?: string;
}

/**
 * A compact, touch-friendly bar that makes the active roster scope and filters
 * immediately clear. Placed directly below IncidentInfoBar and above the metric
 * row on the Headcount and Not Accounted pages. Derived entirely from the same
 * scope/filter state used to render the people list — it never holds its own
 * display-only scope value.
 */
const CurrentRosterViewBar: React.FC<CurrentRosterViewBarProps> = ({
  scopeLabel,
  count,
  countNoun = 'people',
  groupLabel,
  filterSummary,
  isNarrowed = false,
  onReset,
  resetLabel = 'Reset',
  onChangeView,
  changeViewLabel = 'Change View',
}) => {
  const detailParts: string[] = [];
  if (groupLabel) detailParts.push(`Group: ${groupLabel}`);
  if (filterSummary) detailParts.push(filterSummary);
  const hasDetails = detailParts.length > 0;
  const detailText = hasDetails ? detailParts.join(' · ') : 'No additional filters';

  const ariaLabel =
    `Viewing ${scopeLabel}, ${count.toLocaleString()} ${countNoun}. ` +
    (hasDetails ? detailParts.join(', ') : 'No additional filters');

  return (
    <Box
      role="region"
      aria-label="Current roster view"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: { xs: 1.5, sm: 2 },
        py: 1,
        mb: 1.5,
        // Light teal, matching the selected-scope accent. Never red.
        bgcolor: `${appColors.primary.main}0A`,
        border: `1px solid ${appColors.primary.main}33`,
        borderRadius: '10px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0.25 }}>
        <Typography component="p" variant="body2" sx={{ color: appColors.text.body }} aria-label={ariaLabel}>
          Viewing:{' '}
          <Box component="span" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            {scopeLabel}
          </Box>
          {' · '}
          <Box component="span" sx={{ fontWeight: 700, color: appColors.primary.dark }}>
            {count.toLocaleString()} {countNoun}
          </Box>
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: hasDetails ? appColors.text.body : appColors.text.caption }}
        >
          {detailText}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
        {onChangeView && (
          <Button
            size="small"
            variant="text"
            startIcon={<TuneIcon />}
            onClick={onChangeView}
            sx={{ textTransform: 'none', fontWeight: 600, color: appColors.primary.dark, minHeight: 40 }}
          >
            {changeViewLabel}
          </Button>
        )}
        {isNarrowed && onReset && (
          <Button
            size="small"
            variant="text"
            startIcon={<RestartAltIcon />}
            onClick={onReset}
            aria-label={`${resetLabel} roster view`}
            sx={{ textTransform: 'none', fontWeight: 600, color: appColors.primary.dark, minHeight: 40 }}
          >
            {resetLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CurrentRosterViewBar;
